import Annotation from "./annotation";
import { drawEllipse, setLineStyle, setFontStyle } from "../canvasUtils";
import { getArcPosition } from "../geometry";

export default class AnalysisETDRS extends Annotation {
  constructor(color, fontColor) {
    super();

    this.canvasSize = null;
    this.bounds = { minX: 0, maxX: 1, minY: 0, maxY: 1 };
    this.offset = null;
    this.analysis = null;
    
    this.color = color || "#2ECE2E";
    this.lineWidth = 2;

    this.font = "16px Calibri";
    this.baseline = "hanging";
    this.fontColor = fontColor || "black";

    this.seAngle = Math.PI / 4;
    this.swAngle = 3 * Math.PI / 4;
  }

  draw(ctx) {
    if (!this.offset || !this.analysis || !this.canvasSize) return;

    let w = this.canvasSize.w * (this.bounds.maxX - this.bounds.minX);
    let h = this.canvasSize.h * (this.bounds.maxY - this.bounds.minY);
    let centerX = this.canvasSize.w * this.offset.x;
    let centerY = this.canvasSize.h * this.offset.y;
    let innerRadiusX = w * this.analysis.centerRadius.x;
    let innerRadiusY = h * this.analysis.centerRadius.y;
    setLineStyle(ctx, this);
    this.drawEllipse(ctx, centerX, centerY, innerRadiusX, innerRadiusY);

    let middleRadiusX = w * this.analysis.middleCircle.radius.x;
    let middleRadiusY = h * this.analysis.middleCircle.radius.y;
    this.drawEllipse(ctx, centerX, centerY, middleRadiusX, middleRadiusY);

    let outerRadiusX = w * this.analysis.outerCircle.radius.x;
    let outerRadiusY = h * this.analysis.outerCircle.radius.y;
    this.drawEllipse(ctx, centerX, centerY, outerRadiusX, outerRadiusY);

    let seInner = getArcPosition(centerX, centerY, innerRadiusX, innerRadiusY, this.seAngle);
    let seOuter = getArcPosition(centerX, centerY, outerRadiusX, outerRadiusY, this.seAngle);
    this.drawSector(ctx, seInner, seOuter);

    let swInner = getArcPosition(centerX, centerY, innerRadiusX, innerRadiusY, this.swAngle);
    let swOuter = getArcPosition(centerX, centerY, outerRadiusX, outerRadiusY, this.swAngle);
    this.drawSector(ctx, swInner, swOuter);

    let neInner = getArcPosition(centerX, centerY, innerRadiusX, innerRadiusY, -this.seAngle);
    let neOuter = getArcPosition(centerX, centerY, outerRadiusX, outerRadiusY, -this.seAngle);
    this.drawSector(ctx, neInner, neOuter);

    let nwInner = getArcPosition(centerX, centerY, innerRadiusX, innerRadiusY, -this.swAngle);
    let nwOuter = getArcPosition(centerX, centerY, outerRadiusX, outerRadiusY, -this.swAngle);
    this.drawSector(ctx, nwInner, nwOuter);

    this.setAppropriateFontSize(ctx, innerRadiusX, middleRadiusX);
    this.drawText(ctx, centerX, centerY, this.analysis.centerAverageThickness);

    let imRadiusX = 0.5 * (middleRadiusX + innerRadiusX);
    let imRadiusY = 0.5 * (middleRadiusY + innerRadiusY);
    this.drawCircleText(ctx, centerX, centerY, imRadiusX, imRadiusY, this.analysis.middleCircle);

    let moRadiusX = 0.5 * (middleRadiusX + outerRadiusX);
    let moRadiusY = 0.5 * (middleRadiusY + outerRadiusY);
    this.drawCircleText(ctx, centerX, centerY, moRadiusX, moRadiusY, this.analysis.outerCircle);
  }

  setAppropriateFontSize(ctx, innerRadiusX, middleRadiusX) {
    let centerText = this.analysis.centerAverageThickness;
    let fText = this.formatText(centerText);
    setFontStyle(ctx, this);
    let width = ctx.measureText(fText).width;
    let innerWidth = innerRadiusX * 2;
    let middleWidth = (middleRadiusX - innerRadiusX);
    let space = Math.min(innerWidth, middleWidth);

    if (width > space) {
      let fontSize = (16 * space / width) - 2;
      ctx.font = fontSize + "px Calibri";
    }
  }

  drawEllipse(ctx, centerX, centerY, radiusX, radiusY) {
    if (radiusX <= 0 || radiusY <= 0) return;
    ctx.beginPath();
    drawEllipse(ctx, centerX, centerY, radiusX, radiusY);
    ctx.closePath();
    ctx.stroke();
  }

  drawSector(ctx, start, end) {
    ctx.beginPath();
    ctx.moveTo(start.x, start.y);
    ctx.lineTo(end.x, end.y);
    ctx.closePath();
    ctx.stroke();
  }

  drawText(ctx, x, y, text) {
    let format = this.formatText(text);
    let width = ctx.measureText(format).width;
    ctx.fillText(format, x - width / 2, y - 7);
  }

  formatText(text) {
    return (text * 1000).toFixed(0);
  }

  drawEllipseText(ctx, centerX, centerY, radiusX, radiusY, angle, text) {
    let { x, y } = getArcPosition(centerX, centerY, radiusX, radiusY, angle);
    this.drawText(ctx, x, y, text);
  }

  drawCircleText(ctx, centerX, centerY, radiusX, radiusY, circle) {
    this.drawEllipseText(ctx, centerX, centerY, radiusX, radiusY, 0, circle.eastValue);
    this.drawEllipseText(ctx, centerX, centerY, radiusX, radiusY, Math.PI / 2, circle.southValue);
    this.drawEllipseText(ctx, centerX, centerY, radiusX, radiusY, -Math.PI / 2, circle.northValue);
    this.drawEllipseText(ctx, centerX, centerY, radiusX, radiusY, Math.PI, circle.westValue);
  }

  updateThicknessAnalysis(analysis) {
    this.analysis = analysis;
    this.signalReady();
  }

  updateFoveaOffset(offset) {
    this.offset = offset;
    this.signalReady();
  }

  updateBounds(bounds) {
    this.bounds = bounds;
    this.signalReady();
  }
}